import type { HelpArticle, HelpBlock } from "../types";

export type GuideStep = { title: string; body: string };
export type GuideFaq = { question: string; answer: string };

export type GuideSpec = {
  id: string;
  title: string;
  description: string;
  tags: string[];
  path: string[];
  lead: string;
  overview: string;
  keyPoints: string[];
  steps: GuideStep[];
  review: string[];
  note?: { variant: "info" | "tip" | "warning" | "note"; title: string; body: string };
  faq: GuideFaq[];
  related: string[];
};

/** Turns audited product copy into the existing typed Help block package.
 * This is content authoring only: it deliberately adds no renderer, visual
 * primitive, or article-specific markup. */
export function createGuideArticle(spec: GuideSpec): HelpArticle {
  const blocks: HelpBlock[] = [
    { kind: "lead", text: spec.lead },
    { kind: "where", path: spec.path },
    { kind: "heading", level: 2, text: "What this controls", id: "overview" },
    { kind: "paragraph", text: spec.overview },
    { kind: "list", style: "unordered", items: spec.keyPoints },
    { kind: "heading", level: 2, text: "Workflow", id: "workflow" },
    { kind: "steps", steps: spec.steps },
    { kind: "heading", level: 2, text: "Review before relying on it", id: "review" },
    { kind: "list", style: "unordered", items: spec.review },
  ];

  if (spec.note) {
    blocks.push({ kind: "callout", ...spec.note });
  }

  blocks.push(
    { kind: "heading", level: 2, text: "Common questions", id: "faq" },
    { kind: "faq", items: spec.faq },
    { kind: "related", topics: spec.related },
  );

  return {
    id: spec.id,
    title: spec.title,
    description: spec.description,
    tags: spec.tags,
    lastUpdated: "July 20, 2026",
    published: true,
    blocks,
  };
}

export function indexGuides(specs: GuideSpec[]): Record<string, HelpArticle> {
  return Object.fromEntries(specs.map((spec) => [spec.id, createGuideArticle(spec)]));
}
